// Date formatting utility - All dates should be in DD/MM/YYYY format

/**
 * Format date string to DD/MM/YYYY
 * @param {string} dateStr - Date in YYYY-MM-DD or ISO format
 * @returns {string} Date in DD/MM/YYYY format
 */
export const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

/**
 * Format date string to DD/MM/YYYY with weekday
 * @param {string} dateStr - Date in YYYY-MM-DD or ISO format
 * @returns {string} Date in "Wednesday, 25/04/2026" format
 */
export const formatDateWithWeekday = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return dateStr;
    const weekday = date.toLocaleDateString('en-IN', { weekday: 'long' });
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${weekday}, ${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

/**
 * Format date string to short format DD/MM
 * @param {string} dateStr - Date in YYYY-MM-DD or ISO format
 * @returns {string} Date in DD/MM format
 */
export const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  } catch {
    return dateStr;
  }
};

/**
 * Get today's date in YYYY-MM-DD format (for date input value)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayYYYYMMDD = () => {
  const now = new Date();
  // Adjust to IST
  const istOffset = 5.5 * 60 * 60 * 1000;
  const ist = new Date(now.getTime() + istOffset);
  return ist.toISOString().split('T')[0];
};

/**
 * Parse DD/MM/YYYY to YYYY-MM-DD (for API calls)
 * @param {string} dateStr - Date in DD/MM/YYYY format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

/**
 * Convert UTC ISO string to IST time (HH:MM)
 * @param {string} utcStr - ISO string in UTC
 * @returns {string} Time in HH:MM format (IST)
 */
export const formatUTCTimeToIST = (utcStr) => {
  if (!utcStr) return '-';
  try {
    const date = new Date(utcStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '-';
  }
};
